// Payroll — API docs §11. Sits on top of attendance (§10) and the salary you
// decide per employee:
//
//   per-day rate   = monthly salary / day basis
//   payable salary = per-day rate * payable days   (from attendance)
//   net payable    = payable salary + bonus - deduction
//   due            = net payable - everything paid so far
//
// Nothing is stored until a month is touched: an untouched month is computed
// live from attendance + salary so it can never be stale. The first adjustment
// or payment snapshots it; once fully PAID it freezes, because a payslip must
// keep saying what was actually paid. Reversing a payment reopens it.
//
// Everything is addressed by (employee, month) — never by a record id — so the
// caller never has to care whether the row exists yet.
import api from "../lib/apiClient";

/* --- salaries (§11.1 – §11.3) ------------------------------------------- */

// §11.1 — set/revise an employee's salary. Revisions are kept, not overwritten:
// back-date `effectiveFrom` to fix an old month or forward-date it to schedule
// a raise. Re-posting the same effectiveFrom corrects that revision instead of
// stacking a duplicate. A month is priced with the revision in force at its end.
//
// NOTE: this is a POST to /payroll/salaries/employees/:id — NOT a PATCH to
// /payroll/employees/:id, which is the bonus/deduction endpoint (§11.5).
export const setEmployeeSalary = (employeeId, payload) =>
  api.post(`/payroll/salaries/employees/${employeeId}`, payload).then((r) => r.data);

// §11.2 — { employee, current, upcoming, history }. `current` is null until a
// salary has been set.
export const getEmployeeSalary = (employeeId) =>
  api.get(`/payroll/salaries/employees/${employeeId}`).then((r) => r.data);

// §11.2 — remove a mis-entered revision.
export const deleteSalaryRevision = (employeeId, revisionId) =>
  api.delete(`/payroll/salaries/employees/${employeeId}/${revisionId}`).then((r) => r.data);

// §11.3 — one row per employee (salarySet, monthlyAmount, effectiveFrom) plus
// totals.monthlyWageBill. Employees without a salary appear with
// salarySet: false so nobody is forgotten.
export const listSalaries = (params = {}) =>
  api.get("/payroll/salaries", { params }).then((r) => r.data);

/* --- the month's sheet and payday (§11.4 – §11.8) ------------------------ */

// §11.4 — the payday screen. `month` is YYYY-MM and defaults to the current
// month; future months are rejected. Rows are sorted by amount due, descending.
export const getPayrollSheet = (params = {}) =>
  api.get("/payroll", { params }).then((r) => r.data);

// §11.4 — a single employee's payslip for a month.
export const getEmployeePayslip = (employeeId, params = {}) =>
  api.get(`/payroll/employees/${employeeId}`, { params }).then((r) => r.data);

// §11.5 — bonus / deduction / note for a month. 409 if the month is already
// fully paid: reverse the payment first (§11.7).
export const adjustPayroll = (employeeId, payload) =>
  api.patch(`/payroll/employees/${employeeId}`, payload).then((r) => r.data);

// §11.6 — mark a month paid. `amount` defaults to the whole outstanding
// balance, so the common case is a body-less call. Partial payments accumulate
// and set PARTIALLY_PAID; the balance settles the month to PAID. Overpaying is
// refused with the outstanding figure.
export const payEmployee = (employeeId, payload = {}) =>
  api.post(`/payroll/employees/${employeeId}/pay`, payload).then((r) => r.data);

// §11.7 — reverse a payment. Recorded as a negative entry on the same ledger
// (nothing is deleted) and the month reopens for editing.
export const reversePayment = (employeeId, payload = {}) =>
  api.post(`/payroll/employees/${employeeId}/reverse`, payload).then((r) => r.data);

// §11.8 — money view over a range of months (YYYY-MM), default last 6, max 24.
// Only covers months that have been processed; an untouched month's liability
// shows up in the sheet (§11.4) instead.
export const getPayrollSummary = (params = {}) =>
  api.get("/payroll/summary", { params }).then((r) => r.data);

// §11.8 — the raw payment ledger.
export const listPayrollHistory = (params = {}) =>
  api.get("/payroll/history", { params }).then((r) => r.data);
